import { useCallback, useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import {
  FALLBACK_LABEL, MODE_LABEL, RETIRED_PLACEMENT_ROLES,
  actualLabel, authorityFor, authorityRoleFor, authorityStateLabel, basisLabel, cancelRequest, handoffLabel, ipadEligibilityLabel,
  latestHandoffFor, loadManagementHosting, moveOutcomeLabel, moveReadiness, moveRequest, normalizePlacement,
  openHandoffFor, placementIsValid, readinessLabel, reasonLabel, reauthorizeOutcomeLabel, reauthorizeRequest,
  refusalLabel, resetPlacementRequest, roleTitle,
  samePlacement, setPlacementRequest, writeOnce,
  type FallbackPolicy, type HostingRead, type PlacementDraft, type PlacementMode, type PlacementRole,
  type PlacementSnapshot, type RelocationSnapshot,
} from '../lib/management-hosting';
import '../management-hosting.css';

type Write = (name: string, request: object, done: (outcome: Awaited<ReturnType<typeof writeOnce>>) => string) => void;

function hostNamer(snapshot: PlacementSnapshot) {
  return (id: string | null): string => (id === null ? 'Unknown host' : snapshot.hosts.find((h) => h.nodeId === id)?.name ?? 'Unknown host');
}

function configuredLabel(role: PlacementRole, name: (id: string | null) => string): string {
  const mode = MODE_LABEL[role.placement.mode];
  const suffix = role.placement.isDefault ? ' (role default)' : '';
  return role.placement.preferredNodeId === null ? mode + suffix : `${mode}: ${name(role.placement.preferredNodeId)}${suffix}`;
}

function authorityLabel(role: string, relocation: RelocationSnapshot | null, name: (id: string | null) => string): string {
  if (relocation === null) return 'Unknown';
  const holder = authorityRoleFor(role);
  const authority = authorityFor(relocation, holder);
  if (holder !== role) {
    // #630: no own authority exists for this role; it runs inside the Machinist pass.
    return authority === null
      ? 'Runs in the Machinist pass · Machinist not claimed by any host yet'
      : `Runs in the Machinist pass · ${name(authority.nodeId)} · ${authorityStateLabel(authority.state)}`;
  }
  return authority === null ? 'Not claimed by any host yet' : `${name(authority.nodeId)} · ${authorityStateLabel(authority.state)}`;
}

/** Read-only truth for one role: the four facts kept apart. */
export function ManagementRoleFacts({ role, snapshot, relocation }: {
  role: PlacementRole;
  snapshot: PlacementSnapshot;
  relocation: RelocationSnapshot | null;
}) {
  const name = hostNamer(snapshot);
  const open = openHandoffFor(relocation, role.role);
  const authority = authorityFor(relocation, authorityRoleFor(role.role));
  return (
    <dl className="management-hosting-facts">
      <div><dt>Configured</dt><dd>{configuredLabel(role, name)}</dd></div>
      <div><dt>Authority</dt><dd>{authorityLabel(role.role, relocation, name)}{authority?.turnInProgress ? ' · turn in progress' : ''}</dd></div>
      <div><dt>Actual</dt><dd>{actualLabel(role.actual, name)}</dd></div>
      <div>
        <dt>Recommended</dt>
        <dd data-recommended={role.decision.recommended ? 'yes' : 'no'}>
          {role.decision.recommended && role.decision.nodeId !== null
            ? `${name(role.decision.nodeId)} · ${basisLabel(role.decision.basis)}`
            : `None: ${reasonLabel(role.decision.reason ?? 'unknown')}`}
        </dd>
      </div>
      {open !== null && <div className="management-hosting-pending"><dt>Move</dt><dd>{handoffLabel(open, name)}</dd></div>}
    </dl>
  );
}

function RoleEditor({ orgId, role, snapshot, relocation, busy, write }: {
  orgId: string;
  role: PlacementRole;
  snapshot: PlacementSnapshot;
  relocation: RelocationSnapshot | null;
  busy: boolean;
  write: Write;
}) {
  const name = hostNamer(snapshot);
  const saved: PlacementDraft = role.placement;
  const [draft, setDraft] = useState<PlacementDraft>(saved);
  const [dirty, setDirty] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');

  // A clean editor follows canonical state (including changes made on the iPad);
  // an owner's unsaved edit is never overwritten by a refresh.
  const savedKey = JSON.stringify(saved);
  useEffect(() => { if (!dirty) setDraft(saved); }, [savedKey, dirty]);

  const edit = (next: PlacementDraft) => { setDraft(next); setDirty(true); };
  const normalized = normalizePlacement(draft);
  const canSave = placementIsValid(normalized) && !samePlacement(normalized, saved);
  const authority = authorityFor(relocation, role.role);
  const open = openHandoffFor(relocation, role.role);
  const last = latestHandoffFor(relocation, role.role);
  const recommended = role.decision.recommended ? role.decision.nodeId : null;
  const toRecommended = moveReadiness(role, authority, recommended);
  const chosen = moveReadiness(role, authority, moveTarget === '' ? null : moveTarget);
  const relocatable = toRecommended.kind !== 'not_relocatable';

  return (
    <div className="stack management-hosting-editor">
      <form className="stack" onSubmit={(event) => {
        event.preventDefault();
        if (!canSave) return;
        write('set_management_role_placement', setPlacementRequest(orgId, role.role, normalized), (outcome) => {
          if (outcome.outcome === 'ok') { setDirty(false); return 'Preference saved. The role has not moved.'; }
          if (outcome.outcome === 'unknown') { setDirty(false); return 'Brunel could not confirm the preference saved. It has been reread; it will not be retried automatically.'; }
          return `Brunel refused that hosting change (${outcome.code}).`;
        });
      }}>
        <label className="stack">
          <span>Mode</span>
          <select aria-label={`${roleTitle(role.role)} placement mode`} value={draft.mode} onChange={(event) => {
            const mode = event.target.value as PlacementMode;
            const fallbackPolicy: FallbackPolicy = mode === 'pinned' ? 'none'
              : mode === 'preferred' && draft.fallbackPolicy === 'none' && saved.mode !== 'preferred' ? 'automatic' : draft.fallbackPolicy;
            edit(normalizePlacement({ ...draft, mode, fallbackPolicy }));
          }}>
            {(Object.keys(MODE_LABEL) as PlacementMode[]).map((mode) => <option key={mode} value={mode}>{MODE_LABEL[mode]}</option>)}
          </select>
        </label>
        {draft.mode !== 'automatic' && (
          <label className="stack">
            <span>Host</span>
            <select aria-label={`${roleTitle(role.role)} preferred host`} value={draft.preferredNodeId ?? ''} onChange={(event) => {
              const id = event.target.value === '' ? null : event.target.value;
              edit({ ...draft, preferredNodeId: id, fallbackNodeIds: draft.fallbackNodeIds.filter((f) => f !== id) });
            }}>
              <option value="">Choose a host</option>
              {snapshot.hosts.map((host) => (
                <option key={host.nodeId} value={host.nodeId}>
                  {host.name}{host.hostKind === 'ipad' ? ' (iPad)' : ''}{host.online ? '' : ' · offline'}
                </option>
              ))}
            </select>
          </label>
        )}
        {draft.mode === 'preferred' && (
          <label className="stack">
            <span>If it is not eligible</span>
            <select aria-label={`${roleTitle(role.role)} fallback policy`} value={draft.fallbackPolicy} onChange={(event) => {
              const policy = event.target.value as FallbackPolicy;
              edit({ ...draft, fallbackPolicy: policy, fallbackNodeIds: policy === 'ordered' ? draft.fallbackNodeIds : [] });
            }}>
              {(Object.keys(FALLBACK_LABEL) as FallbackPolicy[]).map((p) => <option key={p} value={p}>{FALLBACK_LABEL[p]}</option>)}
            </select>
          </label>
        )}
        {draft.mode === 'preferred' && draft.fallbackPolicy === 'ordered' && (
          <div className="stack">
            <span>Fallback order (tried top to bottom; only these hosts are used)</span>
            <ol className="management-hosting-fallbacks">
              {draft.fallbackNodeIds.map((id, index) => (
                <li key={id} className="row wrap">
                  <span>{name(id)}</span>
                  <button type="button" disabled={index === 0} onClick={() => {
                    const order = [...draft.fallbackNodeIds];
                    [order[index - 1], order[index]] = [order[index]!, order[index - 1]!];
                    edit({ ...draft, fallbackNodeIds: order });
                  }}>Up</button>
                  <button type="button" onClick={() => edit({ ...draft, fallbackNodeIds: draft.fallbackNodeIds.filter((f) => f !== id) })}>Remove</button>
                </li>
              ))}
            </ol>
            <select aria-label={`${roleTitle(role.role)} add fallback host`} value="" disabled={draft.fallbackNodeIds.length >= 8}
              onChange={(event) => { if (event.target.value !== '') edit({ ...draft, fallbackNodeIds: [...draft.fallbackNodeIds, event.target.value] }); }}>
              <option value="">Add fallback host</option>
              {snapshot.hosts.filter((h) => h.nodeId !== draft.preferredNodeId && !draft.fallbackNodeIds.includes(h.nodeId))
                .map((host) => <option key={host.nodeId} value={host.nodeId}>{host.name}</option>)}
            </select>
          </div>
        )}
        <small className="muted">Automatic lets Brunel choose. Preferred falls back as chosen. Pinned never falls back: if the host is not eligible the role is unavailable. Saving changes the preference only; a running role is not moved.</small>
        <div className="row wrap">
          <button type="submit" className="primary" disabled={busy || !canSave}>Save preference</button>
          <button type="button" disabled={busy || role.placement.isDefault} onClick={() => {
            write('set_management_role_placement', resetPlacementRequest(orgId, role.role), (outcome) => {
              if (outcome.outcome === 'ok') { setDirty(false); return 'Reset to the role default. The role has not moved.'; }
              if (outcome.outcome === 'unknown') { setDirty(false); return 'Brunel could not confirm the reset. It has been reread; it will not be retried automatically.'; }
              return `Brunel refused that hosting change (${outcome.code}).`;
            });
          }}>Reset to role default</button>
          {dirty && <button type="button" disabled={busy} onClick={() => { setDraft(saved); setDirty(false); }}>Discard edit</button>}
        </div>
      </form>

      {relocatable && (
        <section className="stack management-hosting-move" aria-label={`${roleTitle(role.role)} move authority`}>
          <h4>Move authority</h4>
          {relocation === null ? (
            <p className="muted">Authority status is unavailable, so moves are off.</p>
          ) : (
            <>
              {open !== null ? (
                <div className="row wrap">
                  <span className="management-hosting-pending">{handoffLabel(open, name)}</span>
                  <button type="button" disabled={busy} onClick={() => {
                    write('cancel_management_role_relocation', cancelRequest(orgId, open.handoffId), (outcome) =>
                      outcome.outcome === 'ok' ? 'Move cancelled. Authority stays with the current host.'
                        : outcome.outcome === 'unknown' ? 'Brunel could not confirm the cancel. Status has been reread.'
                        : `Cancel refused: ${refusalLabel(outcome.code)}`);
                  }}>Cancel move</button>
                </div>
              ) : last !== null && <small className="muted">{handoffLabel(last, name)}</small>}
              {recommended !== null && recommended !== authority?.nodeId && (
                <div className="row wrap">
                  <button type="button" className="primary" disabled={busy || toRecommended.kind !== 'ready'} onClick={() => {
                    write('request_management_role_relocation', moveRequest(orgId, role.role, recommended),
                      (outcome) => moveOutcomeLabel(outcome, name(recommended)));
                  }}>Relocate now to {name(recommended)}</button>
                  {toRecommended.kind !== 'ready' && <small className="muted">{readinessLabel(toRecommended, name)}</small>}
                </div>
              )}
              <div className="row wrap">
                <select aria-label={`${roleTitle(role.role)} move target`} value={moveTarget} onChange={(event) => setMoveTarget(event.target.value)}>
                  <option value="">Move to a host…</option>
                  {snapshot.hosts.map((host) => <option key={host.nodeId} value={host.nodeId}>{host.name}</option>)}
                </select>
                <button type="button" disabled={busy || chosen.kind !== 'ready'} onClick={() => {
                  if (chosen.kind !== 'ready') return;
                  write('request_management_role_relocation', moveRequest(orgId, role.role, chosen.targetNodeId),
                    (outcome) => moveOutcomeLabel(outcome, name(chosen.targetNodeId)));
                }}>Move now</button>
                {moveTarget !== '' && <small className="muted">{readinessLabel(chosen, name)}</small>}
              </div>
            </>
          )}
          <small className="muted">Moving asks the current host to hand off at a safe point. Authority changes only when the destination accepts; a running turn is never interrupted, and the role&apos;s runtime and model are never substituted.</small>
          {authority !== null && authority.nodeId === recommended && open === null && (
            <>
              <button type="button" disabled={busy || authority.turnInProgress} onClick={() => {
                write('reauthorize_management_role_authority',
                  reauthorizeRequest(orgId, role.role, authority.authorityGeneration),
                  reauthorizeOutcomeLabel);
              }}>Re-authorize current route</button>
              <small className="muted">Use this after an intentional route change stranded authority on the same host. Brunel accepts only the exact configured route when it is available and advances the authority generation.</small>
            </>
          )}
        </section>
      )}

      <section className="stack" aria-label={`${roleTitle(role.role)} hosts`}>
        <h4>Hosts for this role</h4>
        <ul className="stack management-hosting-evaluations">
          {role.evaluations.map((evaluation) => {
            const host = snapshot.hosts.find((h) => h.nodeId === evaluation.nodeId) ?? null;
            const ipad = host === null ? null : ipadEligibilityLabel(host);
            return (
              <li key={evaluation.nodeId} data-eligible={evaluation.eligible ? 'yes' : 'no'}>
                <strong>{name(evaluation.nodeId)}</strong>
                {evaluation.current && <span className="muted"> · Actual</span>}
                {evaluation.nodeId === authority?.nodeId && <span className="muted"> · Authority</span>}
                <span> · {evaluation.eligible ? 'Eligible' : 'Not eligible'}</span>
                {evaluation.reasons.length > 0 && <div><small>{evaluation.reasons.map(reasonLabel).join(' · ')}</small></div>}
                {ipad !== null && <div><small className="muted">{ipad}</small></div>}
                {evaluation.unknown.length > 0 && <div><small className="muted">Unknown: {evaluation.unknown.join(', ')}</small></div>}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

/**
 * Settings → Connections → Management hosting. The same canonical placement and
 * relocation RPCs as the iPad; rereads on every normal desk refresh so changes
 * made on the iPad appear here and web changes appear there.
 */
export function ManagementHostingSettings({ client, guard, orgId, revision, onlyRoles, startOpen = false }: {
  client: SupabaseClient;
  guard: Guard;
  orgId: string;
  /** Changes on each normal desk refresh; triggers a canonical reread. */
  revision: number;
  /** When set, only these placement roles are shown. */
  onlyRoles?: readonly string[];
  /** Open each role disclosure. Who does what uses this inside its one Advanced toggle. */
  startOpen?: boolean;
}) {
  const [read, setRead] = useState<HostingRead | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setRead(await loadManagementHosting(client, orgId));
  }, [client, orgId]);
  useEffect(() => { void reload(); }, [reload, revision]);

  // Direct verified-owner mutations, like the Planner Host: the existing owner
  // step-up gate, exactly one attempt, then a reread of canonical state.
  const write: Write = (name, request, done) => {
    void guard('post_owner_command', async () => {
      setBusy(true);
      setMessage(null);
      try {
        const outcome = await writeOnce(client, name, request);
        setMessage(done(outcome));
        await reload();
      } finally {
        setBusy(false);
      }
    });
  };

  if (read === null) return <p className="muted">Reading management hosting…</p>;
  if (read.placements === null) return <p role="alert">{read.error}</p>;
  const { placements, relocation } = read;

  return (
    <section className="stack management-hosting-settings" aria-label="Management hosting">
      <p className="muted">
        Configured is where you want a role hosted. Authority is where it is hosted now; Actual is what last ran.
        Saving a preference does not move a role. Move now asks the current host for a handoff, and the role moves
        only when the destination accepts. iPad and web read and write the same canonical settings.
      </p>
      {read.relocationError !== null && <p role="note" className="management-hosting-pending">{read.relocationError}</p>}
      {message !== null && <p role="alert">{message}</p>}
      <ul className="stack management-hosting-roles" aria-label="Management roles">
        {placements.roles.filter((role) => (
          !RETIRED_PLACEMENT_ROLES.includes(role.role)
          && (onlyRoles === undefined || onlyRoles.includes(role.role))
        )).map((role) => (
          <li key={role.role} className="stack management-hosting-role" data-role={role.role}>
            <details open={startOpen ? true : undefined}>
              <summary>
                <strong>{roleTitle(role.role)}</strong>
                <ManagementRoleFacts role={role} snapshot={placements} relocation={relocation} />
              </summary>
              <RoleEditor orgId={orgId} role={role} snapshot={placements} relocation={relocation} busy={busy} write={write} />
            </details>
          </li>
        ))}
      </ul>
      {placements.hosts.some((h) => h.hostKind === 'ipad') && (
        <small className="muted">iPad hosts are management-only and never run Workers or add Worker capacity. They are eligible only while Brunel is in the foreground there.</small>
      )}
    </section>
  );
}
